/**
 * Collection Carousel Web Component
 * Infinite loop carousel with responsive items per view
 * Mobile: 3 items visible, Desktop: 6 items visible
 */

if (!customElements.get('collection-carousel')) {
    class CollectionCarousel extends HTMLElement {
        constructor() {
            super();
            this.track = null;
            this.items = [];
            this.prevButton = null;
            this.nextButton = null;
            this.currentIndex = 0;
            this.isAnimating = false;
            this.itemsPerView = 3;
            this.totalItems = 0;
            this.resizeObserver = null;
        }

        connectedCallback() {
            this.track = this.querySelector('[data-carousel-track]');
            this.items = Array.from(this.querySelectorAll('[data-carousel-item]'));
            this.prevButton = this.querySelector('[data-carousel-prev]');
            this.nextButton = this.querySelector('[data-carousel-next]');
            this.totalItems = this.items.length;

            if (!this.track || this.items.length === 0) return;

            this.init();
        }

        disconnectedCallback() {
            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
            }
            window.removeEventListener('resize', this.handleResize);
        }

        init() {
            // Clone items for infinite loop
            this.setupInfiniteLoop();

            // Set up event listeners
            this.prevButton?.addEventListener('click', () => this.navigate('prev'));
            this.nextButton?.addEventListener('click', () => this.navigate('next'));

            // Handle keyboard navigation
            this.prevButton?.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.navigate('prev');
                }
            });

            this.nextButton?.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.navigate('next');
                }
            });

            // Handle responsive changes
            this.handleResize = this.handleResize.bind(this);
            this.mediaQuery = window.matchMedia('(min-width: 750px)');
            this.mediaQuery.addEventListener('change', this.handleResize);
            this.handleResize();

            // Handle transition end for infinite loop reset
            this.track.addEventListener('transitionend', () => this.onTransitionEnd());
        }

        setupInfiniteLoop() {
            // Clone first and last set of items for seamless looping
            const itemsToClone = Math.min(6, this.totalItems);

            // Clone last items and prepend
            for (let i = this.totalItems - 1; i >= Math.max(0, this.totalItems - itemsToClone); i--) {
                const clone = this.items[i].cloneNode(true);
                clone.setAttribute('aria-hidden', 'true');
                clone.setAttribute('data-clone', 'prepend');
                this.track.insertBefore(clone, this.track.firstChild);
            }

            // Clone first items and append
            for (let i = 0; i < Math.min(itemsToClone, this.totalItems); i++) {
                const clone = this.items[i].cloneNode(true);
                clone.setAttribute('aria-hidden', 'true');
                clone.setAttribute('data-clone', 'append');
                this.track.appendChild(clone);
            }

            // Update items array to include clones
            this.allItems = Array.from(this.querySelectorAll('[data-carousel-item]'));
            this.cloneCount = itemsToClone;

            // Set initial position to show original items (after prepended clones)
            this.currentIndex = this.cloneCount;
            this.updatePosition(false);
        }

        handleResize() {
            this.itemsPerView = this.mediaQuery.matches ? 6 : 3;
            this.updatePosition(false);
        }

        navigate(direction) {
            if (this.isAnimating) return;
            this.isAnimating = true;

            if (direction === 'next') {
                this.currentIndex++;
            } else {
                this.currentIndex--;
            }

            this.updatePosition(true);
        }

        updatePosition(animate = true) {
            if (!this.track || this.allItems.length === 0) return;

            const item = this.allItems[0];
            const itemStyle = getComputedStyle(item);
            const itemWidth = item.offsetWidth;
            const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
            const moveDistance = (itemWidth + gap) * this.currentIndex;

            if (animate) {
                this.track.style.transition = 'transform 0.5s ease-in-out';
            } else {
                this.track.style.transition = 'none';
            }

            this.track.style.transform = `translateX(-${moveDistance}px)`;
        }

        onTransitionEnd() {
            this.isAnimating = false;

            // Check if we need to reset position for infinite loop
            const maxIndex = this.cloneCount + this.totalItems;
            const minIndex = this.cloneCount;

            if (this.currentIndex >= maxIndex) {
                // Moved past the end, jump to beginning
                this.currentIndex = minIndex + (this.currentIndex - maxIndex);
                this.updatePosition(false);
            } else if (this.currentIndex < minIndex) {
                // Moved before the beginning, jump to end
                this.currentIndex = maxIndex - (minIndex - this.currentIndex);
                this.updatePosition(false);
            }
        }
    }

    customElements.define('collection-carousel', CollectionCarousel);
}

